import { useEffect, useState } from "react";
import { api, idr } from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { Input } from "@/components/ui/input";
import { History, Search, ArrowDown, ArrowUp, Minus, Globe } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

export default function PriceHistory() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");

  const load = (q = "") => api.get(`/price-history${q ? `?search=${encodeURIComponent(q)}` : ""}`).then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => { const t = setTimeout(() => load(search), 350); return () => clearTimeout(t); }, [search]);

  // hitung perubahan harga beli vs entri sebelumnya untuk produk yang sama
  const withTrend = rows.map((r, i) => {
    const prev = rows.slice(i + 1).find((x) => x.product_name.toLowerCase() === r.product_name.toLowerCase());
    let trend = null;
    if (prev && prev.purchase_price) {
      const diff = r.purchase_price - prev.purchase_price;
      trend = { diff, pct: (diff / prev.purchase_price) * 100 };
    }
    return { ...r, trend };
  });

  const productCounts = {};
  rows.forEach((r) => { productCounts[r.product_name] = (productCounts[r.product_name] || 0) + 1; });
  const topProduct = Object.entries(productCounts).sort((a, b) => b[1] - a[1])[0]?.[0];
  const chartData = rows
    .filter((r) => r.product_name === topProduct)
    .slice().reverse()
    .map((r) => ({ date: new Date(r.created_at).toLocaleDateString("id-ID", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }), harga: r.purchase_price }));

  return (
    <div data-testid="price-history-page">
      <PageHeader overline="Analitik" title="Riwayat Harga Distributor" />
      <div className="p-8">
        {chartData.length >= 2 && (
          <div className="border border-border bg-card rounded-sm p-6 mb-6" data-testid="price-chart">
            <div className="overline text-muted-foreground mb-1">Tren Harga Beli — {topProduct}</div>
            <div className="h-64 mt-4">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(214 22% 88%)" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "hsl(218 12% 46%)" }} />
                  <YAxis tickFormatter={(v) => (v / 1000000).toFixed(1) + "jt"} tick={{ fontSize: 11, fill: "hsl(218 12% 46%)" }} width={48} />
                  <Tooltip formatter={(v) => idr(v)} contentStyle={{ borderRadius: 4, fontSize: 12, border: "1px solid hsl(214 22% 88%)" }} />
                  <Line type="monotone" dataKey="harga" stroke="#F0592B" strokeWidth={2.5} dot={{ r: 4, fill: "#F0592B" }} activeDot={{ r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
        <div className="relative max-w-sm mb-6">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="price-history-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari produk…" className="pl-9 rounded-sm bg-secondary" />
        </div>

        <div className="border border-border rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary">
              <tr className="text-left overline text-muted-foreground">
                <th className="px-4 py-3">Produk</th>
                <th className="px-4 py-3">Harga Beli (Distributor)</th>
                <th className="px-4 py-3">Perubahan</th>
                <th className="px-4 py-3">Margin</th>
                <th className="px-4 py-3">Harga Jual</th>
                <th className="px-4 py-3">Sumber</th>
                <th className="px-4 py-3">Tanggal</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {withTrend.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-muted-foreground">
                  <History className="w-6 h-6 mx-auto mb-2 opacity-50" />Belum ada riwayat. Gunakan AI Assistant untuk mencari harga.</td></tr>
              )}
              {withTrend.map((r) => (
                <tr key={r.id} className="hover:bg-secondary/40" data-testid={`price-row-${r.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium">{r.product_name}</div>
                    <div className="text-xs text-muted-foreground">{r.series}</div>
                  </td>
                  <td className="px-4 py-3 font-mono">{idr(r.purchase_price)}</td>
                  <td className="px-4 py-3">
                    {!r.trend ? <span className="text-muted-foreground text-xs">—</span> :
                      r.trend.diff === 0 ? <span className="inline-flex items-center gap-1 text-muted-foreground text-xs"><Minus className="w-3 h-3" /> tetap</span> :
                      r.trend.diff > 0 ? <span className="inline-flex items-center gap-1 text-destructive text-xs"><ArrowUp className="w-3 h-3" /> {idr(r.trend.diff)} ({r.trend.pct.toFixed(1)}%)</span> :
                      <span className="inline-flex items-center gap-1 text-emerald-600 text-xs"><ArrowDown className="w-3 h-3" /> {idr(Math.abs(r.trend.diff))} ({Math.abs(r.trend.pct).toFixed(1)}%)</span>}
                  </td>
                  <td className="px-4 py-3">{r.margin_percent}%</td>
                  <td className="px-4 py-3 font-mono">{idr(r.unit_price)}</td>
                  <td className="px-4 py-3">
                    {r.realtime ? <span className="inline-flex items-center gap-1 text-primary text-xs"><Globe className="w-3 h-3" /> Web ({r.source_count})</span> : <span className="text-muted-foreground text-xs">Estimasi AI</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString("id-ID")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
